import dayjs from 'dayjs';
import { InjectionKey, ref } from 'vue';
import { ToggleMylistReq } from '@/@types/MyList';
import { useProgram } from '@/composables/useProgram';
import { SCHEDULE_TYPE_CODES } from '@/constants/Schedule';
import { MylistItem } from '@/modules/MylistItem';
import { Program, _Program } from '@/modules/Program';
import { Remind } from '@/modules/Remind';
import { RepositoryFactory } from '@/repositories/RepositoryFactory';
import { MylistRepository } from '@/repositories/mylistRepository';
import { UserRepository } from '@/repositories/userRepository';
import { datetimeFormat } from '@/utils';

const repositories = RepositoryFactory.get('mylist') as MylistRepository;
const userRepository = RepositoryFactory.get('user') as UserRepository;

export type MyList = Map<
  string,
  {
    id: number | null;
    liked: boolean;
    remind: boolean;
  }
>;

export const useMyList = () => {
  /**
   * - マイリストのグローバルデータ
   * - useMylist()のみで扱う
   */
  const myListData = ref<MyList>(new Map());

  /**
   * - グローバルデータをセットするメソッド
   * - アプリ起動時に一度だけ発火
   * @param myListMap
   */
  const setMyListData = (myListMap: MyList) => {
    myListData.value = myListMap;
  };

  /**
   *
   * - グローバルマイリストに番組が存在したらlikeの更新
   * - 存在しなければ新たに追加
   *
   * @param userId
   * @param program
   *
   */
  const toggleMyListState = async (userId: number, program: ToggleMylistReq) => {
    if (myListData.value?.get(program.id)) {
      const bancolleId = myListData.value.get(program.id)?.id as number;

      // delete失敗した時のためのセーブデータ
      const saveData = myListData.value.get(program.id);
      myListData.value.delete(program.id);

      try {
        await repositories.deleteItem(userId, bancolleId);
      } catch (e) {
        // 失敗した場合はフロントのデータを戻す
        if (saveData) myListData.value.set(program.id, saveData);
        throw e;
      }
    } else {
      myListData.value?.set(program.id, { id: null, liked: true, remind: true });

      try {
        const addRes = await repositories.addItem(userId, program);

        // ブラウザデータにbancolleIdをセット
        const bancolleId = addRes.find((myListProgram) => myListProgram.channel.channelSignatureName === program.id)
          ?.id as number;
        myListData.value?.set(program.id, { id: bancolleId, liked: true, remind: true });
      } catch (e) {
        // フロント側に追加した番組を取り消す
        myListData.value.delete(program.id);
        throw e;
      }
    }
  };

  /**
   * - マイバンコレの物理削除
   * @param userId
   * @param bancolleId
   */
  const deleteMyList = async (userId: number, bancolleId: number) => {
    await repositories.deleteItem(userId, bancolleId);
  };

  /**
   * - サムネ付きのお気に入りされている番組の配列を取得
   * - デフォルトでお気に入り登録順にソートする
   * @param {number} userId
   */
  const getLikedItems = async (userId: number) => {
    const items = await repositories.getAllMyList(userId);

    const sortedPrograms = items.sort((a: MylistItem, b: MylistItem) => b.priority - a.priority);

    return sortedPrograms;
  };

  /**
   * 登録順にソート
   */
  const sortByLiked = (myListItems: MylistItem[]) => {
    const result = myListItems.sort((a: MylistItem, b: MylistItem) => b.id - a.id);
    return result;
  };

  /**
   * スタート時間順にソート
   * 開始時間がない場合は、最後尾にPriority順（お気に入り順）でソート
   */
  const sortByStartAt = (myListItems: MylistItem[]) => {
    // startを書き換えたMyListItemのMap
    const replacedItems: Map<number, MylistItem> = new Map();

    const hasFuture: MylistItem[] = [];
    const nonFuture: MylistItem[] = [];
    myListItems.forEach((program) => {
      if (!program.futurePrograms?.length) {
        nonFuture.push(program);
        return;
      }

      // start時間がないものを時間付きでクローンする処理
      if (!program.futurePrograms[0].start?.trim().length) {
        const clone = program.cloneWith({});

        if (!clone.futurePrograms) return;
        clone.futurePrograms[0].start = _formatStart(clone.futurePrograms[0], dayjs().toDate());
        replacedItems.set(program.id, program);
        hasFuture.push(clone);
        return;
      }

      hasFuture.push(program);
    });

    const result = [
      ...hasFuture.sort((a: MylistItem, b: MylistItem) => {
        if (!a.futurePrograms || !b.futurePrograms) {
          throw new Error('今後の放送予定がありません');
        }
        return Number(a.futurePrograms[0]?.start) - Number(b.futurePrograms[0]?.start);
      }),
      ...nonFuture.sort((a: MylistItem, b: MylistItem) => b.id - a.id),
    ];

    // ソートの為にデータ加工したインスタンスを元に戻す
    replacedItems.forEach((originalItem, id) => {
      const targetIndex = result.findIndex((item) => {
        item.id === id;
      });

      if (targetIndex === -1) return;
      const newData = result[targetIndex].cloneWith({ futurePrograms: originalItem.futurePrograms });

      result.splice(targetIndex, 1, newData);
    });

    return result;
  };

  /**
   * - リマインドのフラグを更新する
   * @param userId
   * @param bancolleId
   * @retrurn void
   */
  const toggleRemindState = async (userId: number, programId: string, myBancolleId: number): Promise<void> => {
    // グローバルデータのリマインドフラグを反転
    const target = myListData.value?.get(programId) as {
      id: number;
      liked: boolean;
      remind: boolean;
    };
    target.remind = !target.remind;

    try {
      // リマインドフラグを更新
      await repositories.toggleRemind(userId, myBancolleId);
    } catch (e) {
      // 失敗した場合はフロントのデータを戻す
      target.remind = !target.remind;
      throw e;
    }
  };

  /**
   * - 番組IDからマイバンコレIDを取得するメソッド
   * @param programId
   */
  const getMyListData = (programId: string) => {
    return myListData.value?.get(programId) || null;
  };

  // 番組がお気に入りされてるかの判定
  const isLiked = (programId: string): boolean => {
    return myListData.value.has(programId);
  };

  // リマインドされているか判定
  const isRemind = (programId: string): boolean => {
    return myListData.value.get(programId)?.remind || false;
  };

  /**
   * programのstartがnullの時のフォーマット処理
   *
   * @param {Program} program
   * @param {Date} today
   * @return {string}
   * @example onairPlan:12月放送予定 → 12/31 23:59:59
   */
  const _formatStart = (program: Program | _Program, today: Date) => {
    if (program.start) return program.start;

    let month = 0;
    if (program.onairPlan?.length === 6) {
      month = Number(program.onairPlan.slice(0, 1));
    } else if (program.onairPlan?.length === 7) {
      month = Number(...[program.onairPlan.slice(0, 2)]);
    }

    const year = today.getMonth() > month ? today.getFullYear() + 1 : today.getFullYear();
    const lastDay = new Date(year, month, 0);

    const startAt = `${year}${month < 10 ? '0' + month : month}${lastDay.getDate()}235959`;
    return startAt;
  };

  /**
   * - 同一番組の今後の放送時間一覧を取得
   *
   */
  const getCurrentProgramList = (programs: Program[], programId: string, currentDate: Date) => {
    const date = dayjs(currentDate).format('YYYYMMDDHHmmss');

    const targetPrograms = programs.filter(
      (program) => program.id === programId && Number(date) < Number(program.start)
    );
    const result = targetPrograms.sort((a: Program, b: Program) => Number(a.start) - Number(b.start));

    return result;
  };

  /**
   * 番組タイトルから番組の種類を示すコードを除外した文字列を返す
   * @return string
   */
  const formatTitle = (title: string | null) => {
    if (!title || title === null) return '';
    let formattedTitle = title;
    for (const code of SCHEDULE_TYPE_CODES) {
      formattedTitle = formattedTitle.replace(code, '');
    }
    return formattedTitle;
  };

  /**
   * - ソートの状態をローカルストレージに保存するメソッド
   */
  const setMyListSortState = (sortState: 'myListSortState1' | 'myListSortState2') => {
    localStorage.setItem('myListSortState', sortState);
  };

  /**
   * - ソートの状態をローカルストレージから取得するメソッド
   */
  const getMyListSortState = () => {
    const data = localStorage.getItem('myListSortState') as 'myListSortState1' | 'myListSortState2' | null;
    return data;
  };

  /**
   * - futureProgramsから直近の放送の詳細テキストを返す
   */
  const getAirPlanState = (futurePrograms: _Program[] | null) => {
    const { isEnded, isStarted } = useProgram();

    const now = dayjs().toDate();

    const startTime =
      futurePrograms === null
        ? '...Loading'
        : !futurePrograms.length
        ? '放送未定'
        : !futurePrograms[0].start || !futurePrograms[0].end
        ? futurePrograms[0].onairPlan
        : !isEnded(futurePrograms[0], now) && isStarted(futurePrograms[0], now)
        ? '現在放送中'
        : futurePrograms[0].start && futurePrograms[0].end
        ? `${datetimeFormat(dayjs(futurePrograms[0]?.start).format('YYYYMMDDHHmmss'))}〜`
        : null;

    const title =
      futurePrograms === null || !futurePrograms.length
        ? null
        : !futurePrograms[0].eptitle || !futurePrograms[0].eptitle.trim().length
        ? formatTitle(futurePrograms[0].title)
        : `[#${Number(futurePrograms[0].episode)}] ${formatTitle(futurePrograms[0].eptitle)}`;

    return { startTime, title };
  };

  /**
   * リマインドの設定を更新する
   */

  const updateRemindSettings = async (userId: number | null, oldRemindData: Remind, currentRemindData: Remind) => {
    if (!userId) throw new Error('UserId is not Provided');

    if (oldRemindData.time !== currentRemindData.time) {
      await userRepository.updateRemindTime(userId, currentRemindData.time);
    }

    if (oldRemindData.channel !== currentRemindData.channel) {
      await userRepository.updateRemindChannel(userId, currentRemindData.channel);
    }
  };

  return {
    toggleMyListState,
    getLikedItems,
    isLiked,
    deleteMyList,
    isRemind,
    toggleRemindState,
    setMyListData,
    getMyListData,
    getCurrentProgramList,
    formatTitle,
    sortByLiked,
    sortByStartAt,
    _formatStart,
    setMyListSortState,
    getMyListSortState,
    getAirPlanState,
    updateRemindSettings,
    myListData,
  };
};

export type UseMyListType = ReturnType<typeof useMyList>;
export const MyListKey: InjectionKey<UseMyListType> = Symbol('UseMyList');
